import React, { useState, useEffect } from "react";
import { useHistory, Redirect } from "react-router-dom";
import moment from "moment";
import { Page, SearchBar } from "components";
import { Row, Col, Badge, Spinner, Table, Button } from "react-bootstrap";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import Header from "../../components/Header/Header";
import AssistenciaCard from "./components/AssistenciaCard";
import { getAssistencias } from "./assistenciasApi";
import ApiGSenha from "services/ApiGSenha";
import {
  FaTools, FaChartLine, FaDollarSign, FaKey, FaBuilding,
  FaArrowUp, FaArrowDown, FaFilter, FaChevronRight, FaPlus
} from "react-icons/fa";

import decodeJwt from "../../hooks/decode";
import { useTheme } from "../../contexts/ThemeContext";

import {
  ASSISTENCIA_DOUGHNUT_COLORS,
  BRAND,
  CHART_SERIES_COLORS as CHART_COLORS,
  KPI,
  getChartGridColor,
  getChartTextColor,
  getDashboardCardSurface,
  getDashboardShellStyles,
  getKpiAccentByIndex
} from 'constants/dashboardColors';
import './Assistencias.css';
const getAssistenciaEmailFilter = (email = "") => {
  if (!email) return "";
  const atIndex = email.indexOf("@");
  return atIndex >= 0 ? email.substring(atIndex) : email;
};

const Assistencias = () => {
  const history = useHistory();
  const { darkMode } = useTheme();
  const dashboardStyles = getDashboardShellStyles(darkMode);
  const userDecode = decodeJwt();

  const isAdmin = userDecode?.permission?.assistencias?.write === true;
  const userAssistenciaId = userDecode?.assistenciaId ? String(userDecode.assistenciaId) : "";

  const [searchData, setSearchData] = useState("");
  const [assistencias, setAssistencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState({}); // { assistenciaId: [{ month, data }] }
  const [selectedMonth, setSelectedMonth] = useState(0); // 0 = current month
  const [viewMode, setViewMode] = useState("dashboard"); // "dashboard" | "cards"

  // Fetch assistências list
  useEffect(() => {
    if (isAdmin) {
      loadAssistencias();
    }
  }, [isAdmin]);

  // Fetch dashboard data for all assistências
  useEffect(() => {
    if (isAdmin && assistencias.length > 0) {
      loadAllData();
      return;
    }

    if (isAdmin && assistencias.length === 0) {
      setAllData({});
      setLoading(false);
    }
  }, [isAdmin, assistencias]);

  const loadAssistencias = async () => {
    try {
      setLoading(true);
      const data = await getAssistencias();
      setAssistencias(data);
      if (data.length === 0) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Erro ao carregar assistências:", error);
      setAssistencias([]);
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    try {
      if (assistencias.length === 0) {
        setAllData({});
        setLoading(false);
        return;
      }

      setLoading(true);
      const result = {};

      for (const assist of assistencias) {
        const months = [];
        const currMonth = moment().month();

        for (let i = 0; i < 6; i++) {
          const m = moment().month(currMonth - i);
          if (m.year() < 2023) continue;

          try {
            const { data } = await ApiGSenha.get("/log/password/assistencia", {
              params: {
                date: `${m.year()}-${m.month()}`,
                email: getAssistenciaEmailFilter(assist.email),
                hp: assist.emailhp || "",
              },
            });
            months.push({ month: m, data });
          } catch {
            months.push({ month: m, data: {} });
          }
        }
        result[assist._id] = months;
      }

      setAllData(result);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for an assistência on a given month index
  const calcTotals = (assistId, monthIdx) => {
    const monthData = allData[assistId]?.[monthIdx]?.data || {};
    const configs = assistencias.find((a) => a._id === assistId) || {};

    const somaTodasAsChaves = monthData.allData?.length || 0;
    const somaChavesCobradas = monthData.filteredData?.length || 0;
    const somaChavesHelpDesk = monthData.assistData?.length || 0;
    const somaChavesLocSolution = monthData.locData?.length || 0;
    const somaChavesReentrada = monthData.reentranceAssisData?.length || 0;

    let somaChaves = somaChavesCobradas - somaChavesHelpDesk - somaChavesLocSolution;
    let somachavesExtraValue = 0;
    if (configs.desconto && somaChaves > 300) {
      somachavesExtraValue = somaChaves - 300;
      somaChaves = 300;
    }

    const total =
      (somaChaves < 300 && configs.desconto
        ? 30000
        : somaChaves * (configs.desconto ? configs.valor - configs.desconto : configs.valor)) +
      somachavesExtraValue * configs.valor +
      somaChavesHelpDesk * configs.valorhp +
      somaChavesLocSolution * configs.valorloc +
      somaChavesReentrada * (configs.valor - configs.valorhp);

    return { total, somaTodasAsChaves, somaChavesCobradas, somaChavesHelpDesk, somaChavesLocSolution, somaChavesReentrada };
  };

  // Aggregate KPIs for selected month
  const getKPIs = () => {
    let totalRevenue = 0;
    let totalKeys = 0;
    let prevRevenue = 0;
    const ativos = assistencias.filter((a) => a.status === "ativo").length;

    assistencias.forEach((a) => {
      const curr = calcTotals(a._id, selectedMonth);
      totalRevenue += curr.total;
      totalKeys += curr.somaTodasAsChaves;

      if (selectedMonth < 5) {
        const prev = calcTotals(a._id, selectedMonth + 1);
        prevRevenue += prev.total;
      }
    });

    const variation = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue) * 100 : 0;
    const avgTicket = ativos > 0 ? totalRevenue / ativos : 0;

    return { totalRevenue, totalKeys, ativos, avgTicket, variation };
  };

  // Chart data: revenue evolution (last 6 months)
  const getRevenueChartData = () => {
    const isDark = document.body.classList.contains("dark-theme");
    const labels = [];
    const datasets = assistencias.map((assist, idx) => ({
      label: assist.nome,
      data: [],
      borderColor: CHART_COLORS[idx % CHART_COLORS.length],
      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + "20",
      fill: true,
      borderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.3,
    }));

    // Build from oldest to newest
    for (let i = 5; i >= 0; i--) {
      const m = allData[assistencias[0]?._id]?.[i]?.month;
      if (m) {
        labels.push(m.format("MMM/YY"));
      } else {
        labels.push("");
      }

      assistencias.forEach((assist, idx) => {
        const { total } = calcTotals(assist._id, i);
        datasets[idx].data.push(total);
      });
    }

    return { labels, datasets };
  };

  // Chart data: category distribution for selected month
  const getCategoryChartData = () => {
    let helpDesk = 0, locSolution = 0, chaves = 0, reentrada = 0;

    assistencias.forEach((a) => {
      const t = calcTotals(a._id, selectedMonth);
      helpDesk += t.somaChavesHelpDesk;
      locSolution += t.somaChavesLocSolution;
      chaves += t.somaChavesCobradas - t.somaChavesHelpDesk - t.somaChavesLocSolution;
      reentrada += t.somaChavesReentrada;
    });

    return {
      labels: ["Chaves Assist.", "Help Desk", "Loc Solution", "Reentrada"],
      datasets: [{
        data: [chaves, helpDesk, locSolution, reentrada],
        backgroundColor: ASSISTENCIA_DOUGHNUT_COLORS,
        borderWidth: 0,
      }],
    };
  };

  // Chart data: comparison bar chart
  const getComparisonChartData = () => {
    const labels = assistencias.map((a) => a.nome);
    const data = assistencias.map((a) => calcTotals(a._id, selectedMonth).total);

    return {
      labels,
      datasets: [{
        label: "Faturamento",
        data,
        backgroundColor: assistencias.map((_, i) => CHART_COLORS[i % CHART_COLORS.length] + "CC"),
        borderColor: assistencias.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
        borderWidth: 1,
      }],
    };
  };

  const isDark = document.body.classList.contains("dark-theme");
  const chartTextColor = getChartTextColor(isDark);
  const chartGridColor = getChartGridColor(isDark);

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: true, labels: { fontColor: chartTextColor, boxWidth: 12, padding: 15 } },
    scales: {
      yAxes: [{
        ticks: { beginAtZero: true, fontColor: chartTextColor, callback: (v) => formatCurrency(v) },
        gridLines: { color: chartGridColor },
      }],
      xAxes: [{
        ticks: { fontColor: chartTextColor },
        gridLines: { color: chartGridColor },
      }],
    },
    tooltips: {
      callbacks: { label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${formatCurrency(item.yLabel)}` },
    },
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      yAxes: [{
        ticks: { beginAtZero: true, fontColor: chartTextColor, callback: (v) => formatCurrency(v) },
        gridLines: { color: chartGridColor },
      }],
      xAxes: [{
        ticks: { fontColor: chartTextColor },
        gridLines: { display: false },
      }],
    },
    tooltips: {
      callbacks: { label: (item) => formatCurrency(item.yLabel) },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: true, position: "bottom", labels: { fontColor: chartTextColor, boxWidth: 12, padding: 15 } },
    cutoutPercentage: 70,
  };

  const formatCurrency = (value) => {
    if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}k`;
    return `R$ ${value.toFixed(0)}`;
  };

  const fullFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

  const filtered = assistencias.filter((item) =>
    [item.nome, item.cidade, item.estado, item.email]
      .some((v) => v && v.toLowerCase().includes(searchData.toLowerCase()))
  );

  const kpis = !loading ? getKPIs() : null;

  const currentMonthLabel = () => {
    const m = moment().subtract(selectedMonth, "months");
    return Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(m.toDate());
  };

  if (!isAdmin) {
    if (userAssistenciaId) {
      return <Redirect to={`/assistencias/${userAssistenciaId}`} />;
    }

    return (
      <Page className="p-3" title="Assistencias">
        <Header page={"Assistencias"} />
        <div className="text-center mt-5" style={{ color: "var(--text-muted)" }}>
          Este usuário não possui nenhuma assistência vinculada.
        </div>
      </Page>
    );
  }

  return (
    <Page className="p-3" title="Assistencias">
      <Header page={"Assistencias"} />

      {/* Header + view toggle */}
      <div className="d-flex align-items-center justify-content-between mb-3 mt-2">
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded"
            style={{ width: 44, height: 44, backgroundColor: `${KPI.primary}15`, color: KPI.primary, borderRadius: 10 }}
          >
            <FaTools size={20} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "var(--text-color)" }}>Painel de Assistências</h5>
            <small style={{ color: "var(--text-muted)" }}>Visão gerencial consolidada</small>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => setViewMode("dashboard")}
            className={`d-flex align-items-center gap-1 assistencias-view-btn ${viewMode === "dashboard" ? "assistencias-view-btn--active" : "assistencias-view-btn--inactive"}`}
          >
            <FaChartLine size={12} /> Dashboard
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => setViewMode("cards")}
            className={`d-flex align-items-center gap-1 assistencias-view-btn ${viewMode === "cards" ? "assistencias-view-btn--active" : "assistencias-view-btn--inactive"}`}
          >
            <FaBuilding size={12} /> Assistências
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => history.push("/assistencias/add")}
            className="d-flex align-items-center gap-1 btn-theme-outline"
          >
            <FaPlus size={12} /> Nova
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 400 }}>
          <Spinner animation="border" variant="success" />
          <small className="mt-3" style={{ color: "var(--text-muted)" }}>Carregando dados de todas as assistências...</small>
        </div>
      ) : viewMode === "cards" ? (
        /* ====== VIEW: CARDS (lista de assistências) ====== */
        <>
          <Row className="mb-3">
            <Col>
              <SearchBar
                onSearch={(value) => setSearchData(value)}
                placeholder="Buscar por nome, cidade ou email..."
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mb-3">
            <Badge bg="success" pill>{assistencias.filter((a) => a.status === "ativo").length} ativo{assistencias.filter((a) => a.status === "ativo").length !== 1 ? "s" : ""}</Badge>
            {assistencias.filter((a) => a.status !== "ativo").length > 0 && (
              <Badge bg="secondary" pill>{assistencias.filter((a) => a.status !== "ativo").length} inativo{assistencias.filter((a) => a.status !== "ativo").length !== 1 ? "s" : ""}</Badge>
            )}
          </div>

          <Row className="g-3">
            {filtered.length === 0 ? (
              <Col>
                <div className="text-center py-5" style={{ color: "var(--text-muted)" }}>Nenhuma assistência encontrada</div>
              </Col>
            ) : (
              filtered.map((assistencia) => (
                <Col xs={12} md={6} lg={4} key={assistencia._id}>
                  <AssistenciaCard
                    assistencia={assistencia}
                    onClick={() => history.push(`/assistencias/${assistencia._id}`)}
                    onEdit={() => history.push(`/assistencias/edit/${assistencia._id}`)}
                    showEdit={isAdmin}
                  />
                </Col>
              ))
            )}
          </Row>
        </>
      ) : (
        /* ====== VIEW: DASHBOARD ====== */
        <div className="assistencias-dashboard" style={dashboardStyles.shell}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4" style={{ ...dashboardStyles.panel, padding: '16px 20px' }}>
            <div>
              <Badge className="mb-2" style={{ background: KPI.secondary, color: '#fff' }}>Assistências</Badge>
              <h5 className="fw-bold mb-1" style={dashboardStyles.titleText}>Painel de Assistências</h5>
              <small style={dashboardStyles.mutedText}>{currentMonthLabel()}</small>
            </div>
          </div>

          {/* Month selector */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <FaFilter size={12} style={{ color: "var(--text-muted)" }} />
            <small className="fw-bold" style={{ color: "var(--text-muted)" }}>Período:</small>
            <div className="d-flex flex-wrap gap-1">
              {[0, 1, 2, 3, 4, 5].map((i) => {
                const m = moment().subtract(i, "months");
                const label = m.format("MMM/YY");
                const isActive = selectedMonth === i;
                return (
                  <Button
                    key={i}
                    variant="outline-success"
                    size="sm"
                    onClick={() => setSelectedMonth(i)}
                    className={`assistencias-period-btn ${isActive ? "assistencias-period-btn--active" : "assistencias-period-btn--inactive"}`}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* KPI Cards */}
          <Row className="g-3 mb-4">
            <Col xs={6} lg={3}>
              <KPICard
                icon={FaDollarSign}
                label="Faturamento Total"
                value={fullFormatter.format(kpis.totalRevenue)}
                sublabel={
                  kpis.variation !== 0 ? (
                    <span style={{ color: kpis.variation > 0 ? KPI.primary : KPI.danger, fontSize: "0.75rem" }}>
                      {kpis.variation > 0 ? <FaArrowUp size={10} /> : <FaArrowDown size={10} />}
                      {" "}{Math.abs(kpis.variation).toFixed(1)}% vs mês anterior
                    </span>
                  ) : null
                }
                accent={getKpiAccentByIndex(0)}
              />
            </Col>
            <Col xs={6} lg={3}>
              <KPICard
                icon={FaKey}
                label="Total de Chaves"
                value={kpis.totalKeys.toLocaleString("pt-BR")}
                sublabel={<span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{currentMonthLabel()}</span>}
                accent={getKpiAccentByIndex(1)}
              />
            </Col>
            <Col xs={6} lg={3}>
              <KPICard
                icon={FaBuilding}
                label="Assistências Ativas"
                value={kpis.ativos}
                sublabel={<span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>de {assistencias.length} cadastradas</span>}
                accent={getKpiAccentByIndex(2)}
              />
            </Col>
            <Col xs={6} lg={3}>
              <KPICard
                icon={FaChartLine}
                label="Ticket Médio"
                value={fullFormatter.format(kpis.avgTicket)}
                sublabel={<span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>por assistência ativa</span>}
                accent={getKpiAccentByIndex(3)}
              />
            </Col>
          </Row>

          {/* Charts Row 1 */}
          <Row className="g-3 mb-4">
            <Col lg={8}>
              <DashboardChartCard
                title="Evolução de Faturamento"
                subtitle="Últimos 6 meses"
                accent={KPI.primary}
                height={300}
              >
                <Line data={getRevenueChartData()} options={lineOptions} />
              </DashboardChartCard>
            </Col>
            <Col lg={4}>
              <DashboardChartCard title="Distribuição por Categoria" accent={KPI.secondary} height={300}>
                <div className="d-flex align-items-center justify-content-center h-100">
                  <Doughnut data={getCategoryChartData()} options={doughnutOptions} />
                </div>
              </DashboardChartCard>
            </Col>
          </Row>

          {/* Charts Row 2 */}
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <DashboardChartCard title="Comparativo de Faturamento" accent={KPI.accent} height={280}>
                <Bar data={getComparisonChartData()} options={barOptions} />
              </DashboardChartCard>
            </Col>
            <Col lg={6}>
              <DashboardChartCard title="Ranking de Assistências" subtitle={currentMonthLabel()} accent={KPI.primary} height="auto" bodyClassName="p-0">
                  <Table className="mb-0" style={{ fontSize: "0.85rem", color: "var(--text-color)", backgroundColor: "transparent" }}>
                    <thead>
                      <tr style={{ backgroundColor: "var(--card-header-bg)", borderBottom: "1px solid var(--card-border)" }}>
                        <th className="px-3 py-2 border-0" style={{ color: "var(--text-muted)", backgroundColor: "transparent" }}>#</th>
                        <th className="px-3 py-2 border-0" style={{ color: "var(--text-muted)", backgroundColor: "transparent" }}>Assistência</th>
                        <th className="px-3 py-2 border-0 text-end" style={{ color: "var(--text-muted)", backgroundColor: "transparent" }}>Chaves</th>
                        <th className="px-3 py-2 border-0 text-end" style={{ color: "var(--text-muted)", backgroundColor: "transparent" }}>Faturamento</th>
                        <th className="px-3 py-2 border-0" style={{ backgroundColor: "transparent" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {assistencias
                        .map((a) => {
                          const t = calcTotals(a._id, selectedMonth);
                          const formatter = new Intl.NumberFormat(a.format || "pt-BR", { style: "currency", currency: a.currency || "BRL" });
                          return { ...a, ...t, formatted: formatter.format(t.total) };
                        })
                        .sort((a, b) => b.total - a.total)
                        .map((a, idx) => (
                          <tr
                            key={a._id}
                            style={{ cursor: "pointer", borderBottom: "1px solid var(--card-border)" }}
                            onClick={() => history.push(`/assistencias/${a._id}`)}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--card-header-bg)"}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ""}
                          >
                            <td className="px-3 py-3 align-middle border-0" style={{ color: "var(--text-muted)", backgroundColor: "transparent" }}>
                              <span
                                className="d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
                                style={{
                                  width: 26, height: 26, fontSize: "0.72rem",
                                  backgroundColor: idx === 0 ? `${KPI.secondary}25` : idx === 1 ? `${KPI.primary}20` : idx === 2 ? `${KPI.accent}25` : "transparent",
                                  color: idx === 0 ? KPI.secondary : idx === 1 ? KPI.primary : idx === 2 ? KPI.accent : "var(--text-muted)",
                                }}
                              >
                                {idx + 1}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-middle border-0" style={{ backgroundColor: "transparent" }}>
                              <div className="fw-bold" style={{ color: "var(--text-color)", fontSize: "0.88rem" }}>{a.nome}</div>
                              <div style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{a.cidade}/{a.estado}</div>
                            </td>
                            <td className="px-3 py-3 align-middle text-end fw-bold border-0" style={{ color: "var(--text-color)", backgroundColor: "transparent" }}>
                              {a.somaTodasAsChaves}
                            </td>
                            <td className="px-3 py-3 align-middle text-end fw-bold border-0" style={{ color: KPI.primary, backgroundColor: "transparent" }}>
                              {a.formatted}
                            </td>
                            <td className="px-3 py-3 align-middle text-end border-0" style={{ backgroundColor: "transparent" }}>
                              <FaChevronRight size={10} style={{ color: "var(--text-muted)" }} />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
              </DashboardChartCard>
            </Col>
          </Row>
        </div>
      )}

    </Page>
  );
};

const KPICard = ({ icon: Icon, label, value, sublabel, accent }) => (
  <div
    className="h-100"
    style={{
      borderRadius: '18px',
      padding: '18px',
      ...getDashboardCardSurface(),
      borderTop: `4px solid ${accent}`
    }}
  >
    <div className="d-flex justify-content-between align-items-start mb-2">
      <div className="flex-grow-1 pe-2">
        <p className="text-uppercase mb-1" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
          {label}
        </p>
        <h2 className="mb-0 fw-bold" style={{ color: 'var(--text-color)', fontSize: '1.25rem', lineHeight: 1.2 }}>
          {value}
        </h2>
        {sublabel && <div className="mt-1">{sublabel}</div>}
      </div>
      <div
        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{ width: 46, height: 46, borderRadius: '14px', background: `${accent}22`, color: accent }}
      >
        <Icon size={20} />
      </div>
    </div>
    <span style={{ display: 'block', width: 52, height: 6, borderRadius: '999px', background: `${accent}55` }} />
  </div>
);

const DashboardChartCard = ({ title, subtitle, accent, height = 300, bodyClassName = '', children }) => (
  <div
    className="h-100"
    style={{
      borderRadius: '18px',
      overflow: 'hidden',
      ...getDashboardCardSurface(),
      borderTop: `4px solid ${accent}`
    }}
  >
    <div
      className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom"
      style={{ backgroundColor: 'var(--card-header-bg)', borderColor: 'var(--card-border) !important' }}
    >
      <span className="fw-bold" style={{ color: BRAND.darkGreen }}>{title}</span>
      {subtitle && <small style={{ color: 'var(--text-muted)' }}>{subtitle}</small>}
    </div>
    <div className={bodyClassName} style={{ height: height === 'auto' ? undefined : height, padding: bodyClassName ? undefined : '12px' }}>
      {children}
    </div>
  </div>
);

export default Assistencias;
